import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchOrders, isToday, fmtTime, getBinSize } from '../services/orderService';
import { flagTask, completeTask } from '../services/taskService';
import { queryKeys } from '../lib/queryClient';
import {
  Text,
  Badge,
  Button,
  Textarea,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import BaseDialog from '../components/BaseDialog';
import {
  CheckmarkCircle24Filled,
  Flag24Regular,
  Chat24Regular,
  Location24Regular,
  Clock24Regular,
  VehicleTruck24Regular,
  ArrowCircleUp24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';

type JobType = 'Delivery' | 'Pickup' | 'Swap';
type JobStatus = 'completed' | 'en_route' | 'upcoming' | 'flagged';

interface Job {
  id: string;
  time: string;
  sortKey: number;
  type: JobType;
  customer: string;
  address: string;
  binSize: string;
  notes?: string;
  status: JobStatus;
}

interface DispatchMessage {
  id: string;
  from: string;
  text: string;
  time: string;
  read: boolean;
}

const JOB_TYPE_COLORS: Record<JobType, { bg: string; fg: string }> = {
  Delivery: { bg: '#E8F4FF', fg: '#1A6FBF' },
  Pickup:   { bg: '#FFF0E6', fg: '#da7901' },
  Swap:     { bg: '#F0EAFF', fg: '#6B3FBF' },
};


const MOCK_MESSAGES: DispatchMessage[] = [
  { id: '1', from: 'Dale', text: 'Job 4 — Maple Leaf moved the bin to the front driveway, not the back. You\'ll see it right away.', time: '10:42 AM', read: false },
  { id: '2', from: 'Dale', text: 'Good work this morning. You\'re on pace.', time: '9:55 AM', read: true },
];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
  },

  // ── Header ──────────────────────────────────────────────────────────
  header: {
    backgroundColor: '#1E3A5F',
    ...shorthands.padding('20px', '20px', '16px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerGreeting: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
  },
  headerSub: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.65)',
    marginTop: '2px',
  },
  jobCountPill: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    ...shorthands.borderRadius('20px'),
    ...shorthands.padding('6px', '14px'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  jobCountNum: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#fff',
    lineHeight: '1',
  },
  jobCountLabel: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.65)',
    fontWeight: '500',
  },

  // ── Messages banner ──────────────────────────────────────────────────
  messagesBanner: {
    backgroundColor: '#da7901',
    ...shorthands.padding('14px', '20px'),
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'pointer',
  },
  messagesBannerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
  },
  messagesBannerFrom: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#fff',
  },
  messagesBannerBody: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.9)',
    lineHeight: '1.4',
  },
  messagesBannerTime: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.6)',
    flexShrink: '0',
  },

  // ── Body ─────────────────────────────────────────────────────────────
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    ...shorthands.padding('16px', '16px', '32px'),
  },

  sectionLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    ...shorthands.padding('4px', '4px', '0'),
  },

  // ── Next job card ─────────────────────────────────────────────────────
  nextCard: {
    backgroundColor: '#1E6FBF',
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('20px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: tokens.shadow8,
  },
  nextCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  nextCardTime: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#fff',
  },
  nextCardType: {
    fontSize: '13px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.75)',
    backgroundColor: 'rgba(255,255,255,0.15)',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('20px'),
  },
  nextCardCustomer: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
    lineHeight: '1.2',
  },
  nextCardAddressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  nextCardAddress: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.85)',
  },
  nextCardNotes: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.75)',
    backgroundColor: 'rgba(0,0,0,0.15)',
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('10px', '12px'),
    lineHeight: '1.4',
  },
  nextCardActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '4px',
  },
  btnComplete: {
    backgroundColor: '#fff',
    color: '#1E6FBF',
    fontWeight: '700',
    fontSize: '15px',
    height: '52px',
    ...shorthands.borderRadius('8px'),
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    '&:hover': { backgroundColor: '#E8F4FF' },
  },
  btnFlag: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    color: '#fff',
    fontWeight: '600',
    fontSize: '15px',
    height: '52px',
    ...shorthands.borderRadius('8px'),
    border: '2px solid rgba(255,255,255,0.35)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
  },

  // ── Regular job card ──────────────────────────────────────────────────
  jobCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    boxShadow: tokens.shadow4,
  },
  jobCardDone: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('14px', '16px'),
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    opacity: '0.55',
  },
  jobCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  jobTime: {
    fontSize: '15px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    minWidth: '72px',
  },
  jobAddress: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    flex: '1',
    lineHeight: '1.3',
  },
  jobCustomer: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  jobCardActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  jobBtnComplete: {
    height: '48px',
    ...shorthands.borderRadius('6px'),
    fontWeight: '600',
    fontSize: '14px',
  },
  jobBtnFlag: {
    height: '48px',
    ...shorthands.borderRadius('6px'),
    fontWeight: '600',
    fontSize: '14px',
  },
  flaggedBadge: {
    backgroundColor: '#FDEAEA',
    color: '#C4314B',
    fontSize: '12px',
    fontWeight: '600',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('20px'),
  },

  // ── Messages section ──────────────────────────────────────────────────
  messageCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('14px', '16px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  messageHeaderTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    flex: '1',
  },
  messageItem: {
    ...shorthands.padding('14px', '16px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '&:last-child': { borderBottom: 'none' },
  },
  messageFrom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  messageFromName: {
    fontSize: '13px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
  },
  messageTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  messageText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
  },
});

function getGreeting(name: string) {
  const hour = new Date().getHours();
  const sal = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  return `${sal}, ${name.split(' ')[0]}`;
}

export default function DriverDashboard() {
  const styles = useStyles();
  const { user } = useAdminAuth();
  const [jobOverrides, setJobOverrides] = useState<Record<string, JobStatus>>({});
  const [messages, setMessages] = useState<DispatchMessage[]>(MOCK_MESSAGES);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [messagesExpanded, setMessagesExpanded] = useState(false);
  const [pendingFlagJob, setPendingFlagJob] = useState<Job | null>(null);
  const [flagNote, setFlagNote] = useState('');
  const qc = useQueryClient();

  const completeMutation = useMutation({
    mutationFn: (taskId: string) => completeTask(taskId),
    onSuccess: (_, taskId) => {
      const orderId = taskId.replace(/-(deliver|return)$/, '');
      setJobOverrides(prev => ({ ...prev, [orderId]: 'completed' }));
    },
  });

  const flagMutation = useMutation({
    mutationFn: ({ taskId, notes }: { taskId: string; notes: string }) =>
      flagTask(taskId, notes),
    onSuccess: (_, { taskId }) => {
      // Derive the order ID from the task ID (strip '-deliver' / '-return' suffix)
      const orderId = taskId.replace(/-(deliver|return)$/, '');
      setJobOverrides(prev => ({ ...prev, [orderId]: 'flagged' }));
      qc.invalidateQueries({ queryKey: ['tasks', 'flagged'] });
      setPendingFlagJob(null);
      setFlagNote('');
    },
  });

  const { data: orders = [] } = useQuery({
    queryKey: queryKeys.orders.all,
    queryFn: fetchOrders,
    staleTime: 5 * 60 * 1000,
  });

  // Build today's job list from real orders (deliveries = starts today, pickups = ends today)
  const baseJobs = useMemo<Job[]>(() => {
    const seen = new Set<string>();
    const result: Job[] = [];
    const statusMap: Record<string, JobStatus> = { started: 'en_route', stopped: 'completed', archived: 'completed' };

    for (const o of orders) {
      const isDelivery = isToday(o.startDate);
      const isPickup = isToday(o.endDate) && !isDelivery;
      if (!isDelivery && !isPickup) continue;
      if (seen.has(o.id)) continue;
      seen.add(o.id);

      const rawDate = isDelivery ? o.startDate : o.endDate;
      result.push({
        id: o.id,
        time: fmtTime(rawDate),
        sortKey: new Date(rawDate).getTime(),
        type: isDelivery ? 'Delivery' : 'Pickup',
        customer: o.customerName,
        address: o.location,
        binSize: getBinSize(o),
        status: statusMap[o.booqableStatus] ?? 'upcoming',
      });
    }

    return result.sort((a, b) => a.sortKey - b.sortKey);
  }, [orders]);

  // Merge in interactive overrides (complete / flag actions)
  const jobs = useMemo<Job[]>(
    () => baseJobs.map(j => ({ ...j, status: jobOverrides[j.id] ?? j.status })),
    [baseJobs, jobOverrides]
  );

  const markComplete = (job: Job) => {
    const suffix = job.type === 'Pickup' ? 'return' : 'deliver';
    completeMutation.mutate(`${job.id}-${suffix}`);
  };

  const openFlagDialog = (job: Job) => {
    setPendingFlagJob(job);
    setFlagNote('');
  };

  const submitFlag = () => {
    if (!pendingFlagJob) return;
    const suffix = pendingFlagJob.type === 'Pickup' ? 'return' : 'deliver';
    flagMutation.mutate({ taskId: `${pendingFlagJob.id}-${suffix}`, notes: flagNote });
  };

  const nextJob = jobs.find(j => j.status === 'upcoming' || j.status === 'en_route');
  const remaining = jobs.filter(j => j.status !== 'completed' && j.status !== 'flagged');
  const done = jobs.filter(j => j.status === 'completed' || j.status === 'flagged');
  const upcomingAfterNext = remaining.filter(j => j.id !== nextJob?.id);
  const unreadMessages = messages.filter(m => !m.read);

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <Text className={styles.headerGreeting}>{getGreeting(user?.name || 'Rico')}</Text>
          <div className={styles.headerSub}>Today · {new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
        </div>
        <div className={styles.jobCountPill}>
          <span className={styles.jobCountNum}>{done.length}/{jobs.length}</span>
          <span className={styles.jobCountLabel}>jobs done</span>
        </div>
      </div>

      {/* Unread dispatch message banner */}
      {!bannerDismissed && unreadMessages.length > 0 && (
        <div className={styles.messagesBanner} onClick={() => setMessages(prev => prev.map(m => ({ ...m, read: true })))}>
          <Chat24Regular style={{ color: '#fff', flexShrink: 0, marginTop: '1px' }} />
          <div className={styles.messagesBannerText}>
            <Text className={styles.messagesBannerFrom}>Dispatch — {unreadMessages[0].from}</Text>
            <Text className={styles.messagesBannerBody}>{unreadMessages[0].text}</Text>
          </div>
          <Text className={styles.messagesBannerTime}>{unreadMessages[0].time}</Text>
          <button
            onClick={e => { e.stopPropagation(); setBannerDismissed(true); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: '0', flexShrink: 0 }}
          >
            <Dismiss24Regular />
          </button>
        </div>
      )}

      <div className={styles.body}>
        {/* No jobs today */}
        {jobs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <VehicleTruck24Regular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
            <Text style={{ display: 'block', fontSize: '16px', fontWeight: '600', marginTop: '12px', color: tokens.colorNeutralForeground2 }}>
              No jobs scheduled for today
            </Text>
          </div>
        )}

        {/* Next job */}
        {nextJob && (
          <>
            <Text className={styles.sectionLabel}>Next up</Text>
            <div className={styles.nextCard}>
              <div className={styles.nextCardTop}>
                <Text className={styles.nextCardTime}>{nextJob.time}</Text>
                <span className={styles.nextCardType}>{nextJob.type} · {nextJob.binSize}</span>
              </div>
              <Text className={styles.nextCardCustomer}>{nextJob.customer}</Text>
              <div className={styles.nextCardAddressRow}>
                <Location24Regular style={{ color: 'rgba(255,255,255,0.7)', fontSize: '18px', flexShrink: 0 }} />
                <Text className={styles.nextCardAddress}>{nextJob.address}</Text>
              </div>
              {nextJob.notes && (
                <Text className={styles.nextCardNotes}>📝 {nextJob.notes}</Text>
              )}
              <div className={styles.nextCardActions}>
                <button className={styles.btnComplete} onClick={() => markComplete(nextJob)}>
                  <CheckmarkCircle24Filled />
                  Mark Complete
                </button>
                <button className={styles.btnFlag} onClick={() => openFlagDialog(nextJob)}>
                  <Flag24Regular />
                  Flag Problem
                </button>
              </div>
            </div>
          </>
        )}

        {/* Upcoming jobs */}
        {upcomingAfterNext.length > 0 && (
          <>
            <Text className={styles.sectionLabel}>Coming up</Text>
            {upcomingAfterNext.map(job => (
              <div key={job.id} className={styles.jobCard}>
                <div className={styles.jobCardTop}>
                  <Text className={styles.jobTime}>{job.time}</Text>
                  <div style={{ flex: 1 }}>
                    <Text className={styles.jobAddress}>{job.address}</Text>
                    <Text className={styles.jobCustomer}>{job.customer}</Text>
                  </div>
                  <Badge
                    appearance="tint"
                    style={{ backgroundColor: JOB_TYPE_COLORS[job.type].bg, color: JOB_TYPE_COLORS[job.type].fg, flexShrink: 0 }}
                  >
                    {job.type} · {job.binSize}
                  </Badge>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Done */}
        {done.length > 0 && (
          <>
            <Text className={styles.sectionLabel}>Done today</Text>
            {done.map(job => (
              <div key={job.id} className={styles.jobCardDone}>
                {job.status === 'completed'
                  ? <CheckmarkCircle24Filled style={{ color: tokens.colorStatusSuccessForeground1, flexShrink: 0 }} />
                  : <Flag24Regular style={{ color: tokens.colorStatusDangerForeground1, flexShrink: 0 }} />
                }
                <div style={{ flex: 1 }}>
                  <Text style={{ fontSize: '14px', fontWeight: '600', color: tokens.colorNeutralForeground2 }}>{job.customer}</Text>
                  <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{job.time} · {job.type} · {job.address}</Text>
                </div>
                {job.status === 'flagged' && (
                  <span className={styles.flaggedBadge}>Problem flagged</span>
                )}
              </div>
            ))}
          </>
        )}

        {/* All jobs done */}
        {remaining.length === 0 && jobs.length > 0 && !jobs.some(j => j.status === 'flagged') && (
          <div style={{ textAlign: 'center', padding: '32px 20px' }}>
            <CheckmarkCircle24Filled style={{ fontSize: '48px', color: tokens.colorStatusSuccessForeground1 }} />
            <Text style={{ display: 'block', fontSize: '18px', fontWeight: '700', marginTop: '12px', color: tokens.colorNeutralForeground1 }}>
              All done for today!
            </Text>
            <Text style={{ display: 'block', fontSize: '14px', color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
              {jobs.length} jobs completed
            </Text>
          </div>
        )}

        {/* Messages */}
        <div className={styles.messageCard}>
          <div
            className={styles.messageHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setMessagesExpanded(prev => !prev)}
          >
            <Chat24Regular style={{ color: tokens.colorNeutralForeground3 }} />
            <Text className={styles.messageHeaderTitle}>Messages from dispatch</Text>
            {unreadMessages.length > 0 && (
              <Badge appearance="filled" color="danger" size="small">{unreadMessages.length} new</Badge>
            )}
            <ArrowCircleUp24Regular
              style={{
                color: tokens.colorNeutralForeground3,
                transform: messagesExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.2s',
              }}
            />
          </div>
          {messagesExpanded && messages.map(msg => (
            <div key={msg.id} className={styles.messageItem}>
              <div className={styles.messageFrom}>
                <Text className={styles.messageFromName}>
                  {msg.from}
                  {!msg.read && (
                    <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: '8px' }}>New</Badge>
                  )}
                </Text>
                <Text className={styles.messageTime}>{msg.time}</Text>
              </div>
              <Text className={styles.messageText}>{msg.text}</Text>
            </div>
          ))}
        </div>

      </div>

      <BaseDialog
        isOpen={pendingFlagJob !== null}
        onClose={() => { setPendingFlagJob(null); setFlagNote(''); }}
        title="Flag a problem"
        maxWidth="420px"
        showCloseButton
        actions={
          <>
            <Button appearance="subtle" onClick={() => { setPendingFlagJob(null); setFlagNote(''); }}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: '#C4314B', borderColor: '#C4314B' }}
              onClick={submitFlag}
              disabled={flagMutation.isPending}
            >
              {flagMutation.isPending ? 'Flagging...' : 'Flag job'}
            </Button>
          </>
        }
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {pendingFlagJob && (
            <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground2 }}>
              {pendingFlagJob.customer} · {pendingFlagJob.address}
            </Text>
          )}
          <Textarea
            placeholder="What's the problem? (optional)"
            value={flagNote}
            onChange={(_, d) => setFlagNote(d.value)}
            rows={4}
            style={{ width: '100%' }}
          />
          {flagMutation.isError && (
            <Text style={{ fontSize: '12px', color: '#C4314B' }}>
              Failed to flag — try again.
            </Text>
          )}
        </div>
      </BaseDialog>
    </div>
  );
}
